import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";

const neonColors = ["#fc03f4", "#03f0fc", "#fc03f4", "#03f0fc", "#fc03f4"];
const cateringImages = Array.from(
  { length: 8 },
  (_, index) => `/images/CateringPicture${index + 1}.webp`,
);

export default function CateringPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentImageIndex((previousIndex) =>
        (previousIndex + 1) % cateringImages.length,
      );
    }, 4000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <>
      <Seo
        title="Catering"
        description="Book Bull on the Loose catering for private parties, corporate lunches, weddings, and community events."
        path="/catering"
        image="/images/CateringPicture1.webp"
      />
      {/* Page Header */}
      <section className="relative pt-[calc(80px+60px)] pb-[60px] px-6 text-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 page-header-gradient-bg"></div>
          <div className="absolute inset-0 hero-grid-bg"></div>
        </div>
        <div className="container">
          <GradientText
            colors={neonColors}
            animationSpeed={6}
            className="text-[clamp(2.5rem,8vw,4.5rem)] mb-4 font-display font-semibold"
          >
            Catering
          </GradientText>
          <p className="text-xl text-text-secondary font-light">
            Let us bring the BBQ to your next event
          </p>
        </div>
      </section>

      {/* Catering Content */}
      <section className="py-[80px] bg-bg-secondary">
        <div className="container">
          <div className="max-w-[900px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-lg-custom">
                <div className="relative h-[300px] lg:h-[400px]">
                  {cateringImages.map((image, index) => (
                    <img
                      key={image}
                      src={image}
                      alt={`Bull on the Loose catering photo ${index + 1}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        index === currentImageIndex ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentImageIndex(
                      (currentImageIndex - 1 + cateringImages.length) %
                        cateringImages.length,
                    )
                  }
                  className="absolute left-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/50"
                  aria-label="Previous catering photo"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M15 19l-7-7 7-7"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentImageIndex(
                      (currentImageIndex + 1) % cateringImages.length,
                    )
                  }
                  className="absolute right-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/50"
                  aria-label="Next catering photo"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M9 5l7 7-7 7"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
                  {cateringImages.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      onClick={() => setCurrentImageIndex(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === currentImageIndex
                          ? "w-8 bg-white"
                          : "w-2.5 bg-white/55 hover:bg-white/80"
                      }`}
                      aria-label={`Show catering photo ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="font-display text-2xl font-semibold text-text-primary mb-6">
                  Make Your Event Unforgettable
                </h2>
                <p className="text-text-secondary text-lg leading-relaxed mb-6">
                  Looking for something special for your next gathering? Bull on
                  the Loose offers full-service catering for private events of
                  all sizes. Whether it's a corporate lunch, graduation party,
                  wedding reception, family reunion, or any celebration, we'll
                  bring our signature pit beef, smoky BBQ, and bold flavors
                  right to you.
                </p>
                <p className="text-text-secondary text-lg leading-relaxed mb-8">
                  We can customize our menu to fit your needs and budget. From
                  intimate gatherings to large events, we've got you covered
                  with fresh, made-to-order food that your guests will love.
                </p>

                <div className="bg-bg-card border border-border-default rounded-xl p-6">
                  <h3 className="font-display font-semibold text-text-primary mb-3">
                    Ready to Start Planning?
                  </h3>
                  <p className="text-text-secondary mb-5">
                    Reach out to us and let's start planning your event. We'd
                    love to hear from you!
                  </p>
                  <Link to="/contact" className="btn btn-primary btn-large">
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
